// src/store/index.js
import axiosInstance from "@/axiosInstance";
import { itemPickerLabel, outstockName } from "@/utilities/itemDescription";
import { createStore } from "vuex";

const store = createStore({
  state: {
    clients: [],
    operators: [],
    items: [],
    outstocks: [],
    quotes: [],
    pagination: { next: null, previous: null },
    draftsCount: null,
    // sc-47879: conteggi delle schede Fuori Magazzino (attivi / arrivati)
    outstockTabCounts: { active: 0, archived: 0 },
    // sc-47952: conteggi delle 5 schede della lista preventivi
    quoteTabCounts: {
      "da-confermare": 0,
      confermati: 0,
      parziali: 0,
      completati: 0,
      archiviati: 0,
    },
  },
  getters: {
    "items/list/filtered": (state) => (category) =>
      (state.items || []).filter((item) => item.category === category) ?? [],
    "items/list": (state) => state.items ?? [],
    "items/pagination": (state) => state.pagination,
    "clients/list": (state) => state.clients ?? [],
    "clients/pagination": (state) => state.pagination,
    "operators/list": (state) => state.operators ?? [],
    "quotes/list": (state) => state.quotes ?? [],
    "quotes/pagination": (state) => state.pagination,
    "quotes/tabCounts": (state) => state.quoteTabCounts,
    "outstocks/list": (state) => state.outstocks ?? [],
    "outstocks/pagination": (state) => state.pagination,
    "outstocks/tabCounts": (state) => state.outstockTabCounts,
    "drafts/count": (state) => state.draftsCount,
  },
  mutations: {
    SET_DRAFTS_COUNT(state, count) {
      state.draftsCount = count;
    },
    SET_OUTSTOCK_TAB_COUNTS(state, counts) {
      state.outstockTabCounts = counts ?? { active: 0, archived: 0 };
    },
    SET_QUOTE_TAB_COUNTS(state, counts) {
      state.quoteTabCounts = counts ?? {
        "da-confermare": 0,
        confermati: 0,
        parziali: 0,
        completati: 0,
        archiviati: 0,
      };
    },
    SET_CLIENTS(state, clients) {
      state.clients = clients;
    },
    // Nuova mutation per aggiungere al volo un cliente
    ADD_CLIENT(state, newClient) {
      state.clients.push(newClient);
    },
    // Nuova mutation per rimuovere un cliente
    REMOVE_CLIENT(state, clientId) {
      state.clients = state.clients.filter((client) => client.id !== clientId);
    },
    // Nuova mutaion per aggiornare un cliente esistente
    UPDATE_CLIENT(state, updatedClient) {
      const index = state.clients.findIndex(
        (client) => client.id === updatedClient.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.clients.splice(index, 1, updatedClient);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_OPERATORS(state, operators) {
      state.operators = operators;
    },
    // Nuova mutation per aggiungere al volo un operatore
    ADD_OPERATOR(state, newOperator) {
      state.operators.push(newOperator);
    },
    // Nuova mutation per rimuovere un operatore
    REMOVE_OPERATOR(state, operatorId) {
      state.operators = state.operators.filter((op) => op.id !== operatorId);
    },
    // Nuova mutaion per aggiornare un operatore esistente
    UPDATE_OPERATOR(state, updatedOperator) {
      const index = state.operators.findIndex(
        (op) => op.id === updatedOperator.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.operators.splice(index, 1, updatedOperator);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_ITEMS(state, items) {
      state.items = items;
    },
    // Nuova mutation per la gestione della paginazione della lista degli articoli
    // SET_PAGINATION(state, pageInfo) {
    //   state.pagination = pageInfo;
    // },

    SET_PAGINATION(state, pageInfo) {
      state.pagination = {
        currentPage: pageInfo.currentPage,
        totalPages: pageInfo.totalPages,
        totalItems: pageInfo.totalItems,
        itemsPerPage: pageInfo.itemsPerPage,
        startItem: (pageInfo.currentPage - 1) * pageInfo.itemsPerPage + 1,
        endItem: Math.min(
          pageInfo.currentPage * pageInfo.itemsPerPage,
          pageInfo.totalItems
        ),
      };
    },
    // Nuova mutation per aggiungere al volo un item
    ADD_ITEM(state, newItem) {
      state.items.push(newItem);
    },
    // Nuova mutation per rimuovere un item
    REMOVE_ITEM(state, itemId) {
      state.items = state.items.filter((item) => item.id !== itemId);
    },
    // Nuova mutaion per aggiornare un item esistente
    UPDATE_ITEM(state, updatedItem) {
      const index = state.items.findIndex((item) => item.id === updatedItem.id);
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.items.splice(index, 1, updatedItem);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_OUTSTOCKS(state, outstocks) {
      state.outstocks = outstocks;
    },
    // Nuova mutation per aggiungere al volo un item
    ADD_OUTSTOCKS(state, newItem) {
      state.outstocks.push(newItem);
    },
    // Nuova mutation per rimuovere un item
    REMOVE_OUTSTOCK(state, itemId) {
      state.outstocks = state.outstocks.filter((item) => item.id !== itemId);
    },
    // Nuova mutaion per aggiornare un item esistente
    UPDATE_OUTSTOCK(state, updatedItem) {
      const index = state.outstocks.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.outstocks.splice(index, 1, updatedItem);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_QUOTES(state, quotes) {
      state.quotes = quotes;
    },
    // Nuova mutation per aggiungere al volo un quote
    ADD_QUOTE(state, newQuote) {
      state.quotes.push(newQuote);
    },
    // Nuova mutation per rimuovere un quote
    REMOVE_QUOTE(state, quoteId) {
      state.quotes = state.quotes.filter((quote) => quote.id !== quoteId);
    },
    // Nuova mutaion per aggiornare un quote esistente
    UPDATE_QUOTE(state, updatedQuote) {
      const index = state.quotes.findIndex(
        (quote) => quote.id === updatedQuote.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.quotes.splice(index, 1, updatedQuote);
      }
    },
    // Per aggiungere documenti al bucket
    ADD_DOC_QUOTE(state, { quoteId, document }) {
      const index = state.quotes.findIndex((q) => q.id === quoteId);
      if (index === -1) return;

      // se non c’è ancora l’array, lo creo
      if (!Array.isArray(state.quotes[index].documents)) {
        // Vue 3: ok aggiungere una nuova proprietà reattiva
        state.quotes[index].documents = [];
      }

      state.quotes[index].documents.push(document);
    },
  },

  actions: {
    async fetchClients({ commit }, { page = 1, limit, search = "" } = {}) {
      try {
        const { data } = await axiosInstance.get("/client/get-all", {
          params: { page, limit, ...(search ? { search } : {}) },
        });
        commit("SET_CLIENTS", Array.isArray(data.results) ? data.results : []);
        commit("SET_PAGINATION", {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results;
      } catch (err) {
        if (err.response?.status === 404) {
          // il server dice “nessun cliente”: trasformalo in array vuoto
          commit("SET_CLIENTS", []);
          // azzera la paginazione per coerenza UI
          commit("SET_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 6,
          });
          return [];
        }
        console.error("fetchClients failed:", err);
        throw err; // lascia passare altri errori (500, 401…)
      }
    },

    // Nuova azione per creare un cliente
    async createClient({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("/client/add", payload);

        // commit("ADD_CLIENT", response.data.operator);
        await dispatch("fetchClients");
        return response;
      } catch (error) {
        console.error("Errore durante la creazione di un nuovo client:", error);
        throw error;
      }
    },

    // Nuova azione per aggiornare un cliente esistente
    async updateClient({ commit }, { clientId, payload }) {
      const response = await axiosInstance.patch(
        `client/update/${clientId}`,
        payload
      );
      commit("UPDATE_CLIENT", response.data.client);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) il cliente
    async archiveClient({ commit }, clientId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`client/archive/${clientId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_CLIENT", clientId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione del cliente: ", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchOperators({ commit }) {
      try {
        const response = await axiosInstance.get("/auth/get-all"); // endpoint della tua API
        commit("SET_OPERATORS", response.data);
      } catch (error) {
        console.error("Errore durante il fetch degli operators:", error);
        throw error;
      }
    },

    // Nuova azione per creare un operatore
    async createOperator({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post(
          "auth/create-operator",
          payload
        );

        // commit("ADD_OPERATOR", response.data.operator);
        await dispatch("fetchOperators");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo operator:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per aggiornare un operatore esistente
    async updateOperator({ commit }, { operatorId, payload }) {
      const response = await axiosInstance.patch(
        `auth/update/${operatorId}`,
        payload
      );
      commit("UPDATE_OPERATOR", response.data.operator);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) l’operatore
    async archiveOperator({ commit }, operatorId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`auth/archive/${operatorId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_OPERATOR", operatorId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione di un operator:", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchItems(
      { commit },
      { page = 1, limit, category = null, search = "" } = {}
    ) {
      try {
        const { data } = await axiosInstance.get("/item/get-all", {
          params: {
            page,
            limit,
            ...(category ? { category } : {}),
            ...(search ? { search } : {}),
          },
        });
        commit("SET_ITEMS", Array.isArray(data.results) ? data.results : []);
        commit("SET_PAGINATION", {
          // first: data.first ?? null,
          // next: data.next ?? null,
          // previous: data.previous ?? null,
          // last: data.last ?? null,
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results; // così il componente può usarlo
      } catch (error) {
        if (error.response?.status === 404) {
          // il server dice “nessun articolo: trasformalo in array vuoto
          commit("SET_ITEMS", []);
          // azzera la paginazione per coerenza UI
          commit("SET_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 6,
          });
          return [];
        }
        // console.error("fetchClients failed:", error);
        throw error; // lascia passare altri errori (500, 401…)
      }
    },

    // Nuova azione per fetchare gli articoli venduti ai clienti
    async fetchClientSoldItems(_, clientId) {
      try {
        const response = await axiosInstance.get(
          `client/${clientId}/client-info`
        );
        return response.data;
      } catch (error) {
        console.error(
          "Errore durante il caricamento degli articoli venduti associati al cliente",
          error
        );
        throw error;
      }
    },

    // Nuova azione per creare un item
    async createItem({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("item/create", payload);

        // commit("ADD_ITEM", response.data.item);
        await dispatch("fetchItems");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo articolo:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per aggiornare un item esistente
    async updateItem({ commit }, { itemId, payload }) {
      const response = await axiosInstance.patch(
        `item/update/${itemId}`,
        payload
      );
      commit("UPDATE_ITEM", response.data.item);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) l’item
    async archiveItem({ commit }, itemId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`item/archive/${itemId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_ITEM", itemId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione dell'articolo:", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchOutstocks(
      { commit },

      {
        page = 1,
        limit,
        category = null,
        search = "",
        state = null,
        // sc-47879: true = scheda "Arrivati" (articoli archiviati all'arrivo)
        archived = false,
      } = {}
    ) {
      try {
        const { data } = await axiosInstance.get("/outstock/get-all", {
          params: {
            page,
            limit,
            ...(category ? { category } : {}),
            ...(search ? { search } : {}),
            ...(state ? { state } : {}),
            archived,
          },
        });
        // console.log("alloutstock", data);
        commit(
          "SET_OUTSTOCKS",
          Array.isArray(data.results) ? data.results : []
        );

        commit("SET_PAGINATION", {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });

        // sc-47879: i conteggi delle due schede viaggiano insieme ai risultati.
        commit("SET_OUTSTOCK_TAB_COUNTS", data.tabCounts ?? null);

        return data.results; // così il componente può usarlo
      } catch (error) {
        console.error(
          "Errore durante il fetch degli articoli fuori magazzino:",
          error
        );

        throw error;
      }
    },

    // Nuova azione per fetchare un articolo non presente magazzino per ID (dettagli)
    async fetchOutstockById(_, outstockId) {
      try {
        const response = await axiosInstance.get(`/outstock/${outstockId}`);
        // commit("SET_OUTSTOCKS", [response.data.outstock]);

        return response.data.outstock;
      } catch (error) {
        console.error(
          "Errore durante il caricamento dell'articolo fuori magazzino per ID:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per creare un item

    async createOutstock({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("outstock/create", payload);
        // commit("ADD_ITEM", response.data.item);
        await dispatch("fetchOutstocks");

        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo articolo:",

          error
        );

        throw error;
      }
    },

    // Nuova azione per aggiornare un item esistente

    // sc-47879: cambia lo stato di approvvigionamento di un articolo fuori magazzino.
    // "Arrivato" archivia l'articolo lato server ed è irreversibile.
    async updateOutstockState(_, { outstockId, state }) {
      const response = await axiosInstance.patch(
        `outstock/state/${outstockId}`,
        {
          state,
        }
      );
      return response;
    },

    async updateOutstock({ commit }, { outstockId, payload }) {
      const response = await axiosInstance.patch(
        `outstock/update/${outstockId}`,
        payload
      );
      commit("UPDATE_OUTSTOCK", response.data.outstock);

      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) l’item

    async archiveOutstock({ commit }, outstockId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(
          `outstock/archive/${outstockId}`
        );
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_OUTSTOCK", outstockId);

        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione dell'articolo:", error);

        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchQuotes(
      { commit },
      { page = 1, limit = 4, search = "", tab = "da-confermare" } = {}
    ) {
      try {
        const { data } = await axiosInstance.get("/quote/get-all", {
          params: {
            page,
            limit,
            ...(search ? { search } : {}),
            tab,
          },
        });
        commit("SET_QUOTES", data.results);
        commit("SET_PAGINATION", {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        // sc-47952: i conteggi delle schede viaggiano con i risultati.
        commit("SET_QUOTE_TAB_COUNTS", data.tabCounts ?? null);
        return data.results; // così il componente può usarlo
      } catch (err) {
        if (err.response?.status === 404) {
          // il server dice “nessun preventivo”: trasformalo in array vuoto
          commit("SET_QUOTES", []);
          throw err;
        }
        console.error("Errore durante il fetch dei preventivi:", err);
        throw err;
      }
    }, // quando verranno usate le rotte del backend

    // Nuova azione per creare un quote
    async createQuote({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("quote/create", payload);

        // commit("ADD_QUOTE", response.data.quote);
        await dispatch("fetchQuotes");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo preventivo:",
          error
        );
        throw error;
      }
    },

    // Carica un documento PDF per un preventivo (solo upload, senza invio email)
    async uploadQuoteDocument({ commit }, { quoteId, document, filename }) {
      try {
        const formData = new FormData();
        formData.append("document", document, filename);

        const response = await axiosInstance.post(
          `/upload/quotes/${quoteId}/documents`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        commit("ADD_DOC_QUOTE", {
          quoteId,
          document: response.data.doc,
        });
        return response;
      } catch (error) {
        console.error("Errore durante l'upload del documento:", error);
        throw error;
      }
    },

    // Invia il PDF già caricato via email al cliente
    async sendQuoteEmail(_, { quoteId, objectName, contact }) {
      const response = await axiosInstance.post(
        `/upload/quotes/${quoteId}/documents/send-email`,
        { objectName, contact }
      );
      return response;
    },

    // Nuova azione per aggiornare un quote esistente
    async updateQuote({ commit }, { quoteId, payload }) {
      const response = await axiosInstance.patch(
        `quote/update-with-delta/${quoteId}`,
        payload
      );
      commit("UPDATE_QUOTE", response.data.quote);
      return response;
    },

    // Imposta lo stato "consegnato" di una singola riga del preventivo
    async setQuoteItemDelivered(_, { quoteItemId, isDelivered }) {
      const response = await axiosInstance.patch(
        `quote/item/${quoteItemId}/delivery`,
        { isDelivered }
      );
      return response;
    },

    // Imposta lo stato "consegnato" di tutti gli articoli di un preventivo
    // in un'unica chiamata (lo stato del preventivo è ricalcolato lato server).
    // sc-47876: il server richiede la lista esplicita degli id da marcare —
    // una PATCH quote-scoped non permetteva di escludere le righe bloccate.
    async setAllQuoteItemsDelivered(_, { quoteId, quoteItemIds, isDelivered }) {
      const response = await axiosInstance.patch(
        `quote/${quoteId}/items/delivery`,
        { quoteItemIds, isDelivered }
      );
      return response;
    },

    // Registra l'arrivo della merce ordinata su una o più righe di preventivo.
    async procureQuoteItems(_, { allocations }) {
      const response = await axiosInstance.post("quote/items/procure", {
        allocations,
      });
      return response;
    },

    // Preventivi in attesa di merce per un articolo di magazzino.
    async fetchPendingProcurement(_, itemId) {
      const { data } = await axiosInstance.get(
        `item/${itemId}/pending-procurement`
      );
      return data.pending || [];
    },

    // Cerca i preventivi che contengono un articolo per nome
    async searchQuotesByItem(_, { name, itemId, outstockId } = {}) {
      // itemId/outstockId → match esatto (click suggerimento); name → testuale.
      const params = {};
      if (itemId != null) params.itemId = itemId;
      else if (outstockId != null) params.outstockId = outstockId;
      else params.name = name;
      const { data } = await axiosInstance.get("quote/search-by-item", {
        params,
      });
      return data.quotes || [];
    },

    // Suggerimenti articoli per l'autocomplete: primi risultati da item/get-all
    // e outstock/get-all, uniti e deduplicati (max `limit`). Nessun commit sullo store.
    async suggestArticles(_, { name, limit = 5 }) {
      const term = (name || "").trim();
      if (!term) return [];

      const grab = (url) =>
        axiosInstance
          .get(url, { params: { page: 1, limit, search: term } })
          .then((r) => (Array.isArray(r.data?.results) ? r.data.results : []))
          .catch(() => []); // 404 = nessun risultato

      const [items, outs] = await Promise.all([
        grab("/item/get-all"),
        grab("/outstock/get-all"),
      ]);

      // Descrizione completa per categoria (sc-48197). Gli endpoint get-all
      // restituiscono gia' tutti i campi: gli articoli a magazzino usano
      // itemPickerLabel ("marca - serie - colore - Tipo Posizione" per i sanitari,
      // "nome - misura" per le piastrelle); i fuori magazzino usano outstockName
      // ("marca - modello"), robusto anche se category non e' valorizzata.
      //
      // name  = etichetta ricca solo per il DISPLAY del suggerimento.
      // term  = valore GREZZO di un singolo campo, usato come termine di ricerca:
      //         la ricerca preventivi fa ILIKE '%term%' sulle singole colonne
      //         (itemName/brand/subBrand), quindi passarle la label composta non
      //         troverebbe nulla. Vedi HomePage.selectSuggestion.
      const norm = (a, source) => ({
        name:
          (source === "stock" ? itemPickerLabel(a) : outstockName(a)) ||
          a.itemName ||
          a.brand ||
          a.subBrand ||
          "—",
        term: a.itemName || a.brand || a.subBrand || "",
        id: a.id,
        source,
      });
      const stock = items.map((a) => norm(a, "stock"));
      const outstock = outs.map((a) => norm(a, "outstock"));

      // interlaccia per equità tra le due fonti, poi dedup per nome
      const merged = [];
      const maxLen = Math.max(stock.length, outstock.length);
      for (let i = 0; i < maxLen; i++) {
        if (stock[i]) merged.push(stock[i]);
        if (outstock[i]) merged.push(outstock[i]);
      }

      const seen = new Set();
      const unique = [];
      for (const s of merged) {
        const key = s.name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(s);
        if (unique.length >= limit) break;
      }
      return unique;
    },

    // Nuova azione per verificare se ci sono preventivi archiviati
    async hasArchivedQuotes() {
      try {
        const { data } = await axiosInstance.get("/quote/get-all", {
          params: { page: 1, limit: 1, archived: true },
        });

        /* nuovo formato: { results: [ … ], pagination: { totalItems, … } } */
        return (data.pagination?.totalItems || 0) > 0;
      } catch (err) {
        console.error(
          "Errore durante il tentantivo di ritrovare i preventivi archiviati: ",
          err
        );
        return false; // fallback sicuro
      }
    },

    // ✅ archivia preventivo
    async archiveQuote({ commit }, quoteId) {
      try {
        const { data } = await axiosInstance.post(`quote/archive/${quoteId}`, {
          archived: true,
        });
        // rimuovo dalla lista corrente (in "attivi" non deve più vedersi)
        commit("REMOVE_QUOTE", quoteId);
        return { data }; // data = { message, quote }
      } catch (error) {
        console.error("Errore durante l'archiviazione del preventivo:", error);
        throw error;
      }
    },

    // ✅ ripristina preventivo (nuova azione)
    async restoreQuote({ commit }, quoteId) {
      try {
        const { data } = await axiosInstance.post(`quote/archive/${quoteId}`, {
          archived: false,
        });
        // rimuovo dalla lista corrente (in "archiviati" non deve più vedersi)
        commit("REMOVE_QUOTE", quoteId);
        return { data }; // data = { message, quote }
      } catch (error) {
        console.error("Errore durante il ripristino del preventivo:", error);
        throw error;
      }
    },

    // sc-48744: eliminazione dalla UI di un preventivo archiviato.
    // Il server valorizza deletedAt: la riga resta a database.
    async deleteQuote({ commit }, quoteId) {
      try {
        const { data } = await axiosInstance.delete(`quote/${quoteId}`);
        commit("REMOVE_QUOTE", quoteId);
        return { data }; // data = { message, quote: { id, deletedAt } }
      } catch (error) {
        console.error("Errore durante l'eliminazione del preventivo:", error);
        throw error;
      }
    },
  },
});

export default store;
